import { ReturnLeadReadable } from '@crm/api'
import { ChangeFieldStatus, CopyableText, LinkInText } from '@crm/components'
import { LeadTasks } from '@crm/features/leads/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { capitalize } from '@crm/utils'
import { getCustomFields } from '@crm/utils/getCustomFields'
import { TagsGroup } from '@shared/components'
import { useLocales } from '@shared/locales'
import { getFullName } from '@shared/utils'
import {
  Button,
  Card,
  Collapse,
  Descriptions,
  Divider,
  Flex,
  Tooltip,
  Typography
} from 'antd'
import { DescriptionsItemType } from 'antd/es/descriptions'
import { Call, CallAdd, Edit2 } from 'iconsax-react'
import { Link } from 'react-router-dom'

type LeadCardProps = {
  lead: ReturnLeadReadable
}

/**
 * The lead serializer does not expose the product an auto-generated lead was
 * created for yet. Delete this extension and read `product_interest` straight
 * off `ReturnLeadReadable` once the field ships and `npm run openapi:crm` has
 * regenerated the client.
 */
type LeadWithProductInterest = ReturnLeadReadable & {
  product_interest?: string | null
}

const getLinkedInUsername = (url?: string | null) => {
  const absentValue = '-'
  if (!url) return absentValue
  const match = url.match(
    /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([a-zA-Z0-9-]+)\/?/
  )

  if (!match) return absentValue

  return match[1]
    .split('-')
    .filter((str) => !/\d/.test(str))
    .map((str) => capitalize(str))
    .join(' ')
}

const descriptionStyles = {
  content: {
    fontWeight: 500
  }
}

export const LeadCard = ({ lead }: LeadCardProps) => {
  const { t, currentLang } = useLocales()
  const navigate = useAppNavigate()

  // `NullEnum` widens the generated union to `unknown`, so the enum value is
  // narrowed back to a string before it is used as a translation key.
  const productOfInterest =
    typeof lead?.product_of_interest === 'string'
      ? lead.product_of_interest
      : undefined

  const leadItems: DescriptionsItemType[] = [
    {
      label: t('contacts.name_lead'),
      children: lead?.name
    },
    {
      label: t('contacts.person_type'),
      children: lead?.lead_type?.name
    },
    {
      label: t('identification_number'),
      children: lead?.contact?.leads?.[0]?.identification_number
    },
    {
      label: t('contacts.sub_industry'),
      children: lead?.sub_industry?.name
    },
    {
      label: t('contacts.country'),
      children: lead?.country?.name
    },
    {
      label: t('contacts.region'),
      children: lead?.region?.name
    },
    {
      label: t('contacts.city'),
      children: lead?.city?.name
    },
    {
      label: t('billing.address'),
      children: lead?.address
    },
    {
      label: t('contacts.reason'),
      children: lead?.reason?.name
    },
    {
      label: t('contacts.source'),
      children: lead?.source?.url ? (
        <Link to={lead.source.url}>{lead?.source?.name}</Link>
      ) : (
        lead?.source?.name
      )
    },
    {
      label: t('contacts.product_of_interest'),
      children: productOfInterest
        ? t(
            `products_of_interest.${productOfInterest}`,
            productOfInterest.toUpperCase()
          )
        : null
    },
    {
      label: t('contacts.responsible_person'),
      children: lead?.owner?.name
    },
    {
      label: t('contacts.comment'),
      children: lead?.comment
    },
    {
      label: t('contacts.website'),
      children: lead?.website ? <LinkInText text={lead?.website} /> : null
    },
    {
      label: t('contacts.linkedin'),
      children: lead?.linkedin ? getLinkedInUsername(lead?.linkedin) : null
    }
  ].filter(({ children }) => children)

  const contactItems: DescriptionsItemType[] = [
    {
      label: t('contacts.full_name'),
      children: getFullName(lead?.contact)
    },
    {
      label: t('contacts.email'),
      children: lead?.contact?.email ? (
        <CopyableText label={lead.contact.email} />
      ) : null
    },
    {
      label: t('contacts.phone_numbers'),
      children: (lead?.contact?.phone_numbers || [])
        .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
        .map((phone, index) => (
          <CopyableText
            key={index}
            icon={!index ? <CallAdd size={16} /> : <Call size={16} />}
            label={
              <Tooltip
                title={
                  !index
                    ? t('lead.primary_phone_number')
                    : t('lead.secondary_phone_number')
                }
              >
                {phone.number}
              </Tooltip>
            }
            text={String(phone?.number)}
          />
        ))
    }
  ].filter(({ children }) => children)

  const customFields = getCustomFields(
    lead?.custom_field_values,
    t,
    currentLang
  )

  return (
    <Card variant="borderless">
      <Flex vertical gap={16}>
        <Flex align="center" justify="space-between" gap={16}>
          <ChangeFieldStatus
            lead={lead}
            leadType={lead.status?.lead_type?.id}
          />

          <Button
            type="text"
            icon={<Edit2 size={20} />}
            onClick={() => navigate(paths.leadsDetails.edit(String(lead?.id)))}
          />
        </Flex>

        <Flex align="center" className="w-full" vertical gap={16}>
          <TagsGroup
            tags={lead?.tags ?? []}
            rows={3}
            showEmptyState={false}
            style={{ textAlign: 'center' }}
          />
        </Flex>
      </Flex>

      {leadItems.length > 0 && (
        <>
          <Divider />

          <Descriptions
            size="small"
            title={t('lead.lead_information')}
            bordered
            colon={false}
            column={1}
            styles={descriptionStyles}
            items={leadItems}
          />
        </>
      )}

      {!!contactItems.length && (
        <>
          <Divider />

          <Descriptions
            title={t('contacts.contact_information')}
            size="small"
            bordered
            layout="vertical"
            colon={false}
            column={1}
            styles={descriptionStyles}
            items={contactItems}
          />
        </>
      )}

      {!!customFields?.length && (
        <>
          <Divider />

          <Collapse
            size="small"
            ghost={true}
            expandIconPosition="end"
            items={[
              {
                key: 'customFields',
                label: (
                  <Typography.Text strong>
                    {t('nomenclature.custom_fields')}
                  </Typography.Text>
                ),
                styles: {
                  body: {
                    padding: 0
                  }
                },
                children: (
                  <Descriptions
                    size="small"
                    bordered
                    colon={false}
                    column={1}
                    items={customFields}
                  />
                )
              }
            ]}
          />
        </>
      )}

      <Divider />

      <LeadTasks lead={lead} />
    </Card>
  )
}
